import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Terminal,
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  CalendarClock,
  Check,
  ClipboardList,
  Loader2,
  MapPin,
  Sparkles,
  Users,
  ExternalLink,
  LayoutGrid,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import LatestScroll from "@/components/LatestScroll";
import { getCaseById, relatedCases } from "@/data/caseStudies";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configurable calendar link (single place to update)
const BOOK_DEMO_URL = "https://calendly.com/deploydnow/demo";

const colorText = {
  emerald: "text-emerald-700",
  amber: "text-amber-700",
  sky: "text-sky-700",
};

const vizColor = {
  emerald: "#16a34a",
  amber: "#d97706",
  sky: "#0284c7",
};

// Big animated visual for the hero of the case study
const HeroViz = ({ type, color }) => {
  const fill = vizColor[color] || vizColor.emerald;
  if (type === "line") {
    return (
      <svg viewBox="0 0 600 160" preserveAspectRatio="none" className="h-full w-full">
        <polyline
          points="0,130 80,120 140,100 200,110 260,80 320,88 380,60 440,45 500,32 600,20"
          fill="none"
          stroke={fill}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="spark-line"
        />
        <circle cx="600" cy="20" r="6" fill={fill} />
      </svg>
    );
  }
  if (type === "heat") {
    const cells = Array.from({ length: 84 });
    return (
      <div className="grid h-full w-full gap-[3px]" style={{ gridTemplateColumns: "repeat(21, minmax(0, 1fr))" }}>
        {cells.map((_, i) => (
          <span
            key={i}
            className="heat-cell rounded-sm"
            style={{
              backgroundColor: fill,
              animationDelay: `${(i % 21) * 0.09 + Math.floor(i / 21) * 0.24}s`,
            }}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="flex h-full w-full items-end justify-between gap-1.5">
      {Array.from({ length: 20 }).map((_, i) => (
        <span
          key={i}
          className="chart-bar w-3 rounded-sm"
          style={{ backgroundColor: fill, animationDelay: `${i * 0.12}s`, opacity: 0.9 }}
        />
      ))}
    </div>
  );
};

// Small inline animated hint (~24px tall) used inside the "After" panel
const MiniViz = ({ type, color }) => {
  const fill = vizColor[color] || vizColor.emerald;
  if (type === "line") {
    return (
      <svg viewBox="0 0 300 24" preserveAspectRatio="none" className="h-full w-full">
        <polyline
          points="0,20 40,18 80,15 120,17 160,10 200,12 240,6 300,3"
          fill="none"
          stroke={fill}
          strokeWidth="2"
          strokeLinecap="round"
          className="spark-line"
        />
      </svg>
    );
  }
  if (type === "heat") {
    return (
      <div className="grid h-full w-full gap-[2px]" style={{ gridTemplateColumns: "repeat(30, minmax(0, 1fr))" }}>
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={i}
            className="heat-cell rounded-sm"
            style={{ backgroundColor: fill, animationDelay: `${i * 0.06}s` }}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="flex h-full w-full items-end justify-between gap-0.5 px-1">
      {Array.from({ length: 30 }).map((_, i) => (
        <span
          key={i}
          className="chart-bar w-1 rounded-sm"
          style={{ backgroundColor: fill, animationDelay: `${i * 0.08}s`, opacity: 0.85 }}
        />
      ))}
    </div>
  );
};

export default function CaseStudyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = getCaseById(id);

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState("");
  const [quoteNote, setQuoteNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!item) {
    return (
      <main className="min-h-screen bg-[#F7F6F1] px-6 py-24 text-center">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-500">
          Case not found.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-emerald-700 hover:text-emerald-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </main>
    );
  }

  const openDemo = () => {
    window.open(BOOK_DEMO_URL, "_blank", "noopener,noreferrer");
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!quoteEmail.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, {
        email: quoteEmail.trim().toLowerCase(),
        note: quoteNote.trim() || null,
        case_id: item.id,
        vendor: item.vendor,
        customer: item.customer,
        request_type: "quote",
      });
      toast.success("Quote request sent.", {
        description: `We'll route it to ${item.vendor} and follow up shortly.`,
      });
      setQuoteOpen(false);
      setQuoteEmail("");
      setQuoteNote("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not submit.", {
        description: typeof detail === "string" ? detail : "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="case-study-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.05) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-emerald-700"
        >
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / case study
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openDemo}
            data-testid="top-book-demo"
            className="group inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 px-3.5 py-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Book a demo</span>
            <span className="sm:hidden">Demo</span>
          </button>
          <button
            type="button"
            onClick={() => setQuoteOpen(true)}
            data-testid="top-get-quote"
            className="group inline-flex items-center gap-2 border border-zinc-300 bg-white px-3.5 py-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-900 transition-colors hover:border-emerald-500 hover:text-emerald-700"
          >
            <ClipboardList className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Get a quote</span>
            <span className="sm:hidden">Quote</span>
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="back-link"
            className="ml-1 inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-emerald-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      </header>

      <section className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-24 pt-10 sm:px-10 sm:pt-14 lg:px-16">
        {/* Latest banner */}
        {item.latest && (
          <div
            data-testid="latest-banner"
            className="mb-10 flex items-center justify-between gap-4 border border-emerald-500/60 bg-emerald-500/10 px-5 py-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-emerald-800"
          >
            <span className="inline-flex items-center gap-3">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Latest use case in production
            </span>
            <span className="hidden text-emerald-700 sm:inline">
              added by <BrandMark size="inline" />
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14">
          {/* MAIN */}
          <article className="reveal min-w-0">
            {/* Header row: eyebrow + verified badge */}
            <div className="flex items-start justify-between gap-4">
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.32em] text-emerald-600">
                {item.vendor} <span className="text-zinc-300">·</span> at
              </p>
              <span
                data-testid="verified-badge"
                className="inline-flex shrink-0 items-center gap-2 border border-emerald-500 bg-white px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-emerald-700 shadow-[0_0_0_3px_rgba(22,163,74,0.08)]"
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Verified
              </span>
            </div>

            {/* Customer name — clickable → SaaS vendor site */}
            <h1
              data-testid="customer-title"
              className="mt-3 font-display text-[clamp(2.5rem,6vw,4.75rem)] font-extrabold leading-[0.98] tracking-tight text-zinc-900"
            >
              <a
                href={item.vendorUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="customer-name-link"
                className="group inline-flex items-baseline gap-3 hover:text-emerald-700"
              >
                {item.customer}
                <ArrowUpRight className="h-6 w-6 text-zinc-300 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-emerald-600 sm:h-8 sm:w-8" />
              </a>
            </h1>

            {/* Customer bio */}
            <p
              data-testid="customer-bio"
              className="mt-6 max-w-2xl font-display text-lg leading-relaxed text-zinc-700 sm:text-xl"
            >
              {item.customerBio}
            </p>

            {/* Meta chips */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Chip icon={<MapPin className="h-3 w-3" />} label={item.industry} accent={item.color} />
              <Chip icon={<Users className="h-3 w-3" />} label={item.size} />
              <Chip icon={<Sparkles className="h-3 w-3" />} label={`Provider: ${item.vendor}`} />
            </div>

            {/* Customer Verified stamp */}
            <div
              data-testid="customer-verified-stamp"
              className="mt-8 flex items-center gap-4 border-2 border-emerald-500 bg-emerald-50 px-5 py-4"
            >
              <BadgeCheck className="h-8 w-8 shrink-0 text-emerald-600" />
              <div>
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-emerald-800">
                  Customer verified
                </p>
                <p className="mt-0.5 font-display text-base font-semibold leading-tight text-emerald-900 sm:text-lg">
                  Metrics, timeline &amp; outcomes confirmed with {item.customer}&apos;s team &mdash; not just the vendor.
                </p>
              </div>
            </div>

            {/* Big animated visualisation (GIF-like) */}
            <div
              data-testid="hero-viz"
              className={`mt-8 h-40 w-full overflow-hidden border border-zinc-200 ${
                item.color === "amber"
                  ? "bg-amber-50"
                  : item.color === "sky"
                  ? "bg-sky-50"
                  : "bg-emerald-50"
              } p-5 sm:h-48`}
            >
              <HeroViz type={item.viz} color={item.color} />
            </div>

            {/* At-a-glance summary card */}
            <div
              data-testid="summary-card"
              className="mt-6 border border-zinc-300 bg-white p-6 sm:p-7"
            >
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
                // at a glance
              </p>
              <p className="mt-3 font-display text-xl leading-snug text-zinc-900 sm:text-2xl">
                {item.summary}
              </p>
            </div>

            {/* Highlight metric cards */}
            <div className="mt-6 grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-3">
              <HighlightCard
                label="Headline outcome"
                value={item.metric}
                sub={item.metricLabel}
                accent={colorText[item.color]}
                bg="bg-emerald-50"
              />
              <HighlightCard
                label="Deployment"
                value={item.timeline}
                sub="kick-off to production"
                icon={<CalendarClock className="h-4 w-4" />}
                bg="bg-amber-50"
              />
              <HighlightCard
                label="Industry"
                value={item.industry}
                sub={item.size}
                icon={<MapPin className="h-4 w-4" />}
                bg="bg-sky-50"
              />
            </div>

            {/* Before → After split (concise) */}
            <section data-testid="section-problem" className="mt-12 grid grid-cols-1 gap-px border border-zinc-200 bg-zinc-200 sm:grid-cols-2">
              <div className="bg-white p-6 sm:p-7">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-red-700">
                  / before
                </p>
                <h2 className="mt-2 font-display text-xl font-extrabold tracking-tight text-zinc-900">
                  The problem
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-zinc-700 sm:text-base">
                  {item.problem}
                </p>
              </div>
              <div data-testid="section-solution" className="bg-white p-6 sm:p-7">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
                  / after
                </p>
                <h2 className="mt-2 font-display text-xl font-extrabold tracking-tight text-zinc-900">
                  What shipped
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-zinc-700 sm:text-base">
                  {item.solution}
                </p>
                {/* small inline animated hint */}
                <div className={`mt-4 h-6 w-full overflow-hidden ${item.color === "amber" ? "bg-amber-50" : item.color === "sky" ? "bg-sky-50" : "bg-emerald-50"}`}>
                  <MiniViz type={item.viz} color={item.color} />
                </div>
              </div>
            </section>

            {/* ROI — compact chip grid */}
            <section data-testid="section-roi" className="mt-12">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-600">
                / ROI &middot; documented
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {item.roi.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 border-l-2 border-emerald-500 bg-emerald-50/70 p-4"
                  >
                    <Check className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="text-sm leading-relaxed text-zinc-900 sm:text-base">{r}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Optional quote */}
            {item.quote && (
              <blockquote
                data-testid="customer-quote"
                className="mt-10 border-l-2 border-emerald-500 bg-white/70 py-5 pl-6 pr-5 backdrop-blur"
              >
                <p className="font-display text-lg italic leading-snug text-zinc-900 sm:text-xl">
                  &ldquo;{item.quote}&rdquo;
                </p>
                {item.quoteAttribution && (
                  <p className="mt-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                    &mdash; {item.quoteAttribution}
                  </p>
                )}
              </blockquote>
            )}

            {/* Compact 'could be better' note */}
            <section data-testid="section-improvements" className="mt-10 border border-dashed border-zinc-300 bg-white/60 p-5">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                / what could have gone better
              </p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-700">
                {item.improvements}
              </p>
            </section>

            {/* Source + More like these */}
            <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-zinc-200 pt-6 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500 sm:flex-row sm:items-center">
              <span className="inline-flex items-center gap-2">
                <ClipboardList className="h-3.5 w-3.5 text-emerald-600" />
                Source:
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-zinc-700 hover:text-emerald-700"
                >
                  {new URL(item.sourceUrl).hostname}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </span>
              <Link
                to={`/feed?industry=${encodeURIComponent(item.industryKey || "")}`}
                data-testid="more-like-these"
                className="group inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 px-4 py-2.5 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                More like these
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </article>

          {/* SIDEBAR — sticky CTAs */}
          <aside className="lg:sticky lg:top-8 lg:h-fit">
            <div className="reveal border border-zinc-300 bg-white p-6 shadow-[0_1px_0_0_rgba(10,10,10,0.04),0_24px_60px_-30px_rgba(10,10,10,0.18)] sm:p-7">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
                // interested in {item.vendor}?
              </p>
              <h3 className="mt-3 font-display text-2xl font-extrabold leading-tight tracking-tight text-zinc-900">
                Talk to {item.vendor}.
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                We&apos;ll route you to the right person &mdash; no sales SDR
                queue, no waiting.
              </p>

              <button
                type="button"
                onClick={openDemo}
                data-testid="book-demo-btn"
                className="group mt-5 flex w-full items-center justify-between border border-zinc-900 bg-zinc-900 px-4 py-3.5 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  Book a demo
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>

              <button
                type="button"
                onClick={() => setQuoteOpen(true)}
                data-testid="get-quote-btn"
                className="group mt-3 flex w-full items-center justify-between border border-zinc-300 bg-white px-4 py-3.5 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-zinc-900 transition-colors hover:border-emerald-500 hover:text-emerald-700"
              >
                <span className="inline-flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Get a quote
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>

              {/* Trust line */}
              <div className="mt-6 border-t border-zinc-200 pt-5">
                <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-emerald-700">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  Verified by <BrandMark size="inline" />
                </p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                  Metrics and outcomes reviewed with both vendor and customer
                  finance ops before publication.
                </p>
              </div>
            </div>

            {/* Meta strip */}
            <div className="mt-6 border border-zinc-200 bg-white/60 p-5 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <MetaRow label="Case ID" value={item.id} />
              <MetaRow label="Vendor" value={item.vendor} />
              <MetaRow label="Industry" value={item.industry} />
              <MetaRow label="Timeline" value={item.timeline} />
            </div>
          </aside>
        </div>
      </section>

      {/* Quote dialog */}
      <Dialog open={quoteOpen} onOpenChange={(o) => !submitting && setQuoteOpen(o)}>
        <DialogContent className="border border-zinc-300 bg-white p-0 sm:max-w-md">
          <div className="p-6 sm:p-8">
            <DialogHeader>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
                // get a quote &middot; {item.vendor}
              </p>
              <DialogTitle className="mt-3 font-display text-2xl font-extrabold leading-snug tracking-tight text-zinc-900">
                Tell us where to send the quote.
              </DialogTitle>
              <DialogDescription className="font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-500">
                We&apos;ll pass your context to {item.vendor}&apos;s account team.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submitQuote} className="mt-6 space-y-4" data-testid="quote-form">
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                  Work email
                </label>
                <input
                  data-testid="quote-email-input"
                  type="email"
                  required
                  autoFocus
                  value={quoteEmail}
                  onChange={(e) => setQuoteEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-2 w-full border border-zinc-300 bg-white px-4 py-3 font-display text-base text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                  Context <span className="text-zinc-400">(optional)</span>
                </label>
                <textarea
                  data-testid="quote-note-input"
                  value={quoteNote}
                  onChange={(e) => setQuoteNote(e.target.value)}
                  placeholder="Size, use case, timeline…"
                  rows={3}
                  className="mt-2 w-full resize-none border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
                />
              </div>
              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={submitting || !quoteEmail}
                className="group flex w-full items-center justify-between border border-zinc-900 bg-zinc-900 px-4 py-3.5 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600 disabled:opacity-60"
              >
                <span>{submitting ? "Sending…" : "Send request"}</span>
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUpRight className="h-4 w-4" />
                )}
              </button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <LatestScroll
        title="More use cases from this industry"
        excludeId={item.id}
        industryKey={item.industryKey}
      />

      <footer className="relative z-10 border-t border-zinc-200 px-6 py-6 sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-start justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 sm:flex-row sm:items-center">
          <span>
            © {new Date().getFullYear()} <BrandMark size="inline" />, Inc.
          </span>
          <span className="inline-flex items-center gap-2">
            <BadgeCheck className="h-3 w-3 text-emerald-600" /> Verified case
            study &middot; every metric reviewed
          </span>
        </div>
      </footer>
    </main>
  );
}

// -- sub-components --

const Chip = ({ icon, label, accent }) => (
  <span
    className={`inline-flex items-center gap-1.5 border ${
      accent ? "border-emerald-500/40 text-emerald-700" : "border-zinc-300 text-zinc-700"
    } bg-white px-2.5 py-1 font-mono-tech text-[10px] uppercase tracking-[0.22em]`}
  >
    {icon}
    {label}
  </span>
);

const HighlightCard = ({ label, value, sub, accent, icon, bg = "bg-white" }) => (
  <div className={`${bg} p-6`}>
    <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
      {icon}
      {label}
    </p>
    <p
      className={`mt-3 font-display text-3xl font-black leading-tight tracking-tight ${
        accent || "text-zinc-900"
      }`}
    >
      {value}
    </p>
    {sub && (
      <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        {sub}
      </p>
    )}
  </div>
);

const BigStat = ({ label, value, accent, icon }) => (
  <div className="bg-white p-6">
    <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
      {icon}
      {label}
    </p>
    <p
      className={`mt-3 font-display text-3xl font-black leading-tight tracking-tight ${
        accent || "text-zinc-900"
      }`}
    >
      {value}
    </p>
  </div>
);

const SectionBlock = ({ num, title, children, hint, testId }) => (
  <section data-testid={testId} className="mt-12 border-t border-zinc-200 pt-8">
    <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-600">
      / {num}
    </p>
    <h2
      className="mt-2 font-display text-2xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-3xl"
      dangerouslySetInnerHTML={typeof title === "string" ? { __html: title } : undefined}
    >
      {typeof title !== "string" ? title : undefined}
    </h2>
    {hint && (
      <p
        className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-400"
        dangerouslySetInnerHTML={{ __html: hint }}
      />
    )}
    <div className="mt-4 text-base leading-relaxed text-zinc-700 sm:text-lg">{children}</div>
  </section>
);

const MetaRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-zinc-200 py-2 last:border-0">
    <span>{label}</span>
    <span className="max-w-[60%] truncate text-right text-zinc-800">{value}</span>
  </div>
);
